import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { authApi, type CurrentUser } from '../api/auth'
import { ApiError } from '../api/client'

interface AuthContextValue {
  status: 'loading' | 'unauthenticated' | 'authenticated' | 'needs-setup'
  user: CurrentUser | null
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthContextValue['status']>('loading')
  const [user, setUser] = useState<CurrentUser | null>(null)

  const refresh = useCallback(async () => {
    try {
      // /status är öppet — säger om första setup är gjord.
      const s = await authApi.status()
      if (!s.setupCompleted) {
        setStatus('needs-setup')
        setUser(null)
        return
      }
      // /me är auth-gated — 401 => inte inloggad
      const me = await authApi.me()
      setUser(me)
      setStatus('authenticated')
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setStatus('unauthenticated')
        setUser(null)
        return
      }
      // Nätfel eller server nere — visa som "unauthenticated" så login-vyn dyker upp
      setStatus('unauthenticated')
      setUser(null)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const logout = useCallback(async () => {
    try { await authApi.logout() } catch { /* proceed anyway */ }
    setUser(null)
    setStatus('unauthenticated')
  }, [])

  return (
    <AuthContext.Provider value={{ status, user, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth måste vara inuti <AuthProvider>')
  return ctx
}
